import type {
  ChargePointWrapper,
  ChargePointCluster,
} from '@/features/charge-points/types/charge-point.types';

interface OverlapListPopupProps {
  items: (ChargePointWrapper | ChargePointCluster)[];
  onSelectItem: (item: ChargePointWrapper | ChargePointCluster) => void;
  onClose: () => void;
}

/**
 * Popup showing list of overlapping markers at same location
 */
export function OverlapListPopup({
  items,
  onSelectItem,
  onClose,
}: OverlapListPopupProps) {
  if (items.length === 0) return null;

  const getItemName = (item: ChargePointWrapper | ChargePointCluster) => {
    if ('count' in item) {
      // It's a cluster
      return `Cluster (${item.count} ${item.count === 1 ? 'point' : 'points'})`;
    } else {
      // It's a charge point
      if (item.type === 'chargePointGroup') {
        return item.chargePointGroup?.name || 'Unnamed Site';
      } else {
        return item.chargePoint?.name || 'Unnamed Charge Point';
      }
    }
  };

  const getOperatorName = (item: ChargePointWrapper | ChargePointCluster) => {
    if ('count' in item) {
      // Clusters don't have operator info
      return null;
    } else {
      // It's a charge point wrapper
      if (item.type === 'chargePointGroup') {
        return item.chargePointGroup?.operatorName;
      } else {
        return item.chargePoint?.operatorName;
      }
    }
  };

  // Get available and total count for an item
  const getItemStats = (item: ChargePointWrapper | ChargePointCluster) => {
    if ('count' in item) {
      // It's a cluster - calculate from colors
      const colors = item.pin?.colors;
      if (colors) {
        const available = colors.green || 0;
        const total =
          (colors.green || 0) +
          (colors.orange || 0) +
          (colors.red || 0) +
          ((colors.grey || 0) + (colors.gray || 0)) +
          (colors.blue || 0);
        return { available, total };
      }
      return { available: 0, total: item.count };
    } else {
      // It's a charge point wrapper
      if (item.type === 'chargePointGroup') {
        const colors = item.pin?.colors;
        if (colors) {
          const available = colors.green || 0;
          const total =
            (colors.green || 0) +
            (colors.orange || 0) +
            (colors.red || 0) +
            ((colors.grey || 0) + (colors.gray || 0)) +
            (colors.blue || 0);
          return { available, total };
        }
        return {
          available: item.chargePointGroup?.availableCount || 0,
          total: item.chargePointGroup?.chargePointCount || 1,
        };
      } else {
        // Single charge point
        const isAvailable = item.pin.color === 'green';
        return { available: isAvailable ? 1 : 0, total: 1 };
      }
    }
  };

  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
      <div className="bg-surface text-ink rounded-2xl shadow-monta-lg ring-1 ring-ink/10 overflow-hidden min-w-[260px] max-w-[320px]">
        {/* Header */}
        <div className="px-4 py-3 border-b border-ink/10 flex items-center justify-between">
          <span className="font-semibold text-sm">
            {items.length} {items.length === 1 ? 'Location' : 'Locations'}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-muted hover:text-ink transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* List */}
        <div className="max-h-80 overflow-y-auto">
          {items.map((item) => (
            <button
              key={'count' in item ? item.id : item.id}
              onClick={() => {
                onSelectItem(item);
                onClose();
              }}
              className="w-full px-4 py-3 hover:bg-ink/5 transition-colors text-left border-b border-ink/5 last:border-b-0"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0 font-medium text-sm text-ink truncate">
                    {getItemName(item)}
                  </div>
                  {/* Stats: available / total */}
                  <div className="flex-shrink-0 text-xs font-semibold tabular-nums">
                    {(() => {
                      const stats = getItemStats(item);
                      return (
                        <span
                          className={
                            stats.available > 0 ? 'text-ok' : 'text-muted'
                          }
                        >
                          {stats.available} / {stats.total}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {/* Operator name */}
                {getOperatorName(item) && (
                  <div className="text-xs text-muted">
                    {getOperatorName(item)}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Arrow pointing down */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
          <div
            className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-surface"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
          />
        </div>
      </div>
    </div>
  );
}
